import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { CaretLeft } from "phosphor-react";
import { createPortal } from "react-dom";

import { SearchInput } from "../../SearchInput";
import { Medias } from "../../../enumns/medias";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useInfiniteColumnValuesQuery } from "./hooks/useInfiniteColumnValuesQuery";
import { Button } from "../../Button";
import { Checkbox } from "../../FormElements/Checkbox";
import { Spinner } from "../../Spinner";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { getColumnValuesToFilter } from "../../../utils/table";

type ColumnFilterProps = {
  isOpen: boolean;
  cacheKey: string;
  dataTrigger: string;
  hasEnableSearch?: boolean;
  searchInputMask?: string;
  searchPlaceholder?: string;
  showSelectAll?: boolean;
  getFilterLabel?: (value: string) => string;
  selectedValues?: string[];
  onClearFilter: () => void;
  onClose: () => void;
  onApplyFilter: (filteredValues: string[]) => void;
  onFetchColumnValues: (page: number, search: string) => Promise<any>;
};

export const ColumnFilterValuesMenu = ({
  isOpen,
  cacheKey,
  dataTrigger,
  hasEnableSearch = true,
  searchInputMask,
  searchPlaceholder = "Buscar",
  showSelectAll = false,
  getFilterLabel,
  selectedValues,
  onClose,
  onClearFilter,
  onApplyFilter,
  onFetchColumnValues,
}: ColumnFilterProps) => {
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const isThereDataIndependentOfTheResearchCarriedOut = useRef(false);
  const menuContainerRef = useRef(null);
  const formRef = useRef(null);

  const { control, formState, handleSubmit, reset, getValues } = useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`);

  const {
    data,
    isSuccess,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
  } = useInfiniteColumnValuesQuery({
    key: cacheKey,
    searchTerm,
    onLoadMoreColumnValues: onFetchColumnValues,
  });

  const hasData = !!data?.pages?.[0].data.length;

  const handleFormSubmit = (checkboxValues: Record<string, boolean>) => {
    onApplyFilter(getColumnValuesToFilter(checkboxValues));
    onClose();
  };

  const onSearchByTerm = (debouncedSearchTerm: string) => {
    setSearchTerm(debouncedSearchTerm);
  };

  const extractItemFromNestedArray = (
    acc: Array<{ id: number; value: string }>,
    { data }: { data: Array<{ id: number; value: string }> }
  ) => {
    return [...acc, ...data];
  };

  const uncheckFormValues = (
    acc: object,
    { value: label }: { value: string }
  ) => {
    return {
      ...acc,
      [label]: false,
    };
  };

  useEffect(() => {
    if (hasData) {
      isThereDataIndependentOfTheResearchCarriedOut.current = hasData;
    }
  }, [hasData, isThereDataIndependentOfTheResearchCarriedOut.current]);

  // Atualizar formulário quando selectedValues mudar (não quando novas páginas são carregadas)
  const previousSelectedValuesRef = useRef<string[] | undefined>(selectedValues);
  const previousPagesLengthRef = useRef<number>(0);

  useEffect(() => {
    if (data?.pages) {
      const currentPagesLength = data.pages.length;
      const selectedValuesChanged = 
        JSON.stringify(previousSelectedValuesRef.current) !== JSON.stringify(selectedValues);
      
      // Só resetar o formulário se selectedValues mudou ou se é a primeira carga de dados
      // Não resetar quando apenas novas páginas são adicionadas
      if (selectedValuesChanged || previousPagesLengthRef.current === 0) {
        // Primeiro, desmarcar todos os checkboxes
        const redefinedFormValues = data.pages
          .reduce(extractItemFromNestedArray, [])
          .reduce(uncheckFormValues, {});

        // Depois, marcar apenas os valores selecionados
        if (selectedValues && selectedValues.length > 0) {
          selectedValues.forEach((selectedValue) => {
            const formattedValue =
              typeof selectedValue === "boolean"
                ? selectedValue === true
                  ? "Sim"
                  : "Não"
                : selectedValue;
            const reFormattedValue = formattedValue.replace("'", "'");
            redefinedFormValues[reFormattedValue] = true;
          });
        }

        reset(redefinedFormValues);
        previousSelectedValuesRef.current = selectedValues;
      }
      
      previousPagesLengthRef.current = currentPagesLength;
    }
  }, [selectedValues, data?.pages, reset]);

  const handleClearFilter = () => {
    const redefinedFormValues = data?.pages
      .reduce(extractItemFromNestedArray, [])
      .reduce(uncheckFormValues, {});

    onClearFilter();
    reset(redefinedFormValues);
  };

  const formatFilterCheckboxKey = (value: string | boolean) => {
    const formattedValue =
      typeof value === "boolean"
        ? value === true
          ? "Sim"
          : "Não"
        : value;
    return formattedValue.replace("'", "'");
  };

  const getLoadedFilterValues = () =>
    data?.pages.reduce(extractItemFromNestedArray, []) ?? [];

  const handleSelectAllVisible = () => {
    const currentValues = { ...getValues() };
    getLoadedFilterValues().forEach(({ value }) => {
      currentValues[formatFilterCheckboxKey(value)] = true;
    });
    reset(currentValues);
  };

  // Limpar seleções não aplicadas quando o popup fechar
  useEffect(() => {
    if (!isOpen && data?.pages) {
      // Resetar formulário para refletir apenas os valores aplicados (selectedValues)
      const redefinedFormValues = data.pages
        .reduce(extractItemFromNestedArray, [])
        .reduce(uncheckFormValues, {});

      if (selectedValues && selectedValues.length > 0) {
        selectedValues.forEach((selectedValue) => {
          const formattedValue =
            typeof selectedValue === "boolean"
              ? selectedValue === true
                ? "Sim"
                : "Não"
              : selectedValue;
          const reFormattedValue = formattedValue.replace("'", "'");
          redefinedFormValues[reFormattedValue] = true;
        });
      }

      reset(redefinedFormValues);
    }
  }, [isOpen, data?.pages, selectedValues, reset]);

  useInfiniteScroll(filterMenuRef, hasNextPage, fetchNextPage);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {createPortal(
            <motion.div
              initial={isMinScreenWidthMD ? false : { opacity: 0 }}
              animate={isMinScreenWidthMD ? false : { opacity: 1 }}
              exit={isMinScreenWidthMD ? {} : { opacity: 0 }}
              ref={menuContainerRef}
              className={clsx("flex flex-col gap-2.5 bg-white", {
                "fixed p-6 top-0 left-0 bottom-0 right-0 z-50":
                  !isMinScreenWidthMD,
                "-left-6 top-12 border absolute border-primary-light shadow-lg w-64 p-4 rounded-lg":
                  isMinScreenWidthMD,
              })}
            >
              {!isMinScreenWidthMD && (
                <button className="inline-block mb-10" onClick={onClose}>
                  <CaretLeft size={32} className="text-grey-700" />
                </button>
              )}

              {hasEnableSearch && (
                <SearchInput
                  disabled={false}
                  placeholder={searchPlaceholder}
                  mask={searchInputMask}
                  defaultSearchTerm={searchTerm}
                  onSearch={onSearchByTerm}
                />
              )}

              {showSelectAll && hasData && !isLoading ? (
                <button
                  type="button"
                  className="text-xs text-left underline text-primary-light"
                  onClick={handleSelectAllVisible}
                >
                  Selecionar todos
                </button>
              ) : null}

              <form
                ref={formRef}
                className="flex flex-col flex-1"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <div className="flex-1 py-3">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {isSuccess &&
                        (data.pages?.[0].data.length === 0 ? (
                          <div className="flex items-center justify-center h-full">
                            <p className="text-xs text-center text-grey-500">
                              Nenhum registro encontrado
                            </p>
                          </div>
                        ) : (
                          <div
                            ref={filterMenuRef}
                            className={clsx(
                              "space-y-4 overflow-auto  scrollbar text-grey-900",
                              {
                                "h-fit max-h-60": isMinScreenWidthMD,
                                "h-80": !isMinScreenWidthMD,
                              }
                            )}
                          >
                            {data.pages?.map(({ data }) => {
                              // remover valores repetidos e nulos dos filtros
                              const dataWithUniqueValues = data.filter(
                                (item, index, array) => {
                                  return (
                                    array.findIndex(
                                      (elem) => elem.value === item.value
                                    ) === index
                                  );
                                }
                              );

                              return dataWithUniqueValues?.map(
                                ({ id, value }) => {
                                  const formattedValue =
                                    typeof value === "boolean"
                                      ? value === true
                                        ? "Sim"
                                        : "Não"
                                      : value;

                                  // essa limpeza é feita devido a um bug no react hook form.
                                  // o RHF não aceita no name do elemento strings com o caractere especial ’
                                  const reFormattedValue =
                                    formattedValue.replace("'", "’");

                                  const isSelectedValue =
                                    selectedValues &&
                                    selectedValues?.length > 0 &&
                                    selectedValues?.find(
                                      (selectedValue) => selectedValue === value
                                    );

                                  const checkboxLabel = getFilterLabel
                                    ? getFilterLabel(String(formattedValue))
                                    : reFormattedValue;

                                  return (
                                    <div key={`${id}-${value}-div`}>
                                      <Controller
                                        name={reFormattedValue}
                                        key={`${id}-${value}-input`}
                                        control={control}
                                        defaultValue={isSelectedValue}
                                        render={({
                                          field: {
                                            value = isSelectedValue,
                                            onChange,
                                          },
                                        }) => {
                                          return (
                                            <Checkbox
                                              label={checkboxLabel}
                                              id={String(
                                                `${id}-${value}-checkbox`
                                              )}
                                              checked={value}
                                              onChange={onChange}
                                              value={reFormattedValue}
                                            />
                                          );
                                        }}
                                      />
                                    </div>
                                  );
                                }
                              );
                            })}
                            {isFetchingNextPage && (
                              <div className="flex justify-center">
                                <Spinner />
                              </div>
                            )}
                          </div>
                        ))}

                      {isError && (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-xs text-center whitespace-pre-wrap text-grey-500">
                            Ocorreu um erro na busca pelos valores da coluna
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {hasData && (
                  <div className="flex flex-col gap-2 align-center">
                    <Button
                      fullWidth
                      type="submit"
                      isLoading={formState.isSubmitting}
                      className={clsx({
                        "mx-auto w-[172px] h-10": isMinScreenWidthMD,
                      })}
                    >
                      Aplicar
                    </Button>
                    <button
                      className="text-xs underline text-primary-light"
                      type="button"
                      onClick={handleClearFilter}
                    >
                      Limpar filtro
                    </button>
                  </div>
                )}
              </form>
            </motion.div>,
            isMinScreenWidthMD
              ? document.querySelector(`[data-trigger=${dataTrigger}]`)!
              : document.body
          )}
        </>
      )}
    </AnimatePresence>
  );
};
